// src/services/storageService.js
import { supabase } from '../lib/supabase';

// Helper function to get year/month path
function getYearMonthPath() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}/${month}`;
}

// Helper function to sanitize filename
function sanitizeFilename(name) {
  if (!name) return 'unnamed_file';
  return name
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export const storageService = {

  // ─── Upload deck file ─────────────────────────────────────────────────────
  async uploadDeckFile(file, userId) {
    try {
      if (!file)   throw new Error('No file provided');
      if (!userId) throw new Error('User ID is required');

      const timestamp     = Date.now();
      const yearMonth     = getYearMonthPath();
      const sanitizedName = sanitizeFilename(file.name);

      // Format: year/month/userId/timestamp-filename
      const filePath = `${yearMonth}/${userId}/${timestamp}-${sanitizedName}`;

      console.log('📤 Uploading deck file:', { userId, fileName: file.name, filePath });

      const { data, error } = await supabase
        .storage
        .from('deck-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });

      if (error) throw error;

      console.log('✅ Deck file uploaded successfully:', data?.path);

      return {
        path:        data?.path,
        fullPath:    data?.fullPath,
        fileName:    file.name,
        fileSize:    file.size,
        fileType:    file.type,
        yearMonth,
        storagePath: filePath,
      };
    } catch (error) {
      console.error('❌ Error uploading deck file:', error);
      throw error;
    }
  },

  // ─── Get signed URL (private, expires in 1 hour) ─────────────────────────
  // Use this EVERYWHERE instead of getPublicUrl.
  // The URL is unique per request and expires — it cannot be shared or accessed
  // without a valid Supabase session.
  async getDeckFileUrl(filePath, expiresIn = 3600) {
    try {
      if (!filePath || filePath === 'null' || filePath === 'undefined') {
        throw new Error('Invalid file path provided');
      }

      console.log('🔗 Generating signed URL for:', filePath);

      const { data, error } = await supabase
        .storage
        .from('deck-files')
        .createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      if (!data?.signedUrl) throw new Error('Failed to generate signed URL');

      console.log('✅ Signed URL generated (expires in', expiresIn, 'seconds)');

      return data.signedUrl;
    } catch (error) {
      console.error('❌ Error generating signed URL:', error);
      throw error;
    }
  },

  // ─── Alias — keep old name working if called elsewhere ───────────────────
  async getDeckFileSignedUrl(filePath, expiresIn = 3600) {
    return this.getDeckFileUrl(filePath, expiresIn);
  },

  // ─── Download file as Blob ────────────────────────────────────────────────
  async downloadDeckFile(filePath) {
    try {
      if (!filePath) throw new Error('File path is required');

      console.log('📥 Downloading deck file:', filePath);

      const { data, error } = await supabase
        .storage
        .from('deck-files')
        .download(filePath);

      if (error) throw error;

      console.log('✅ Deck file downloaded successfully');

      return data;
    } catch (error) {
      console.error('❌ Error downloading deck file:', error);
      throw error;
    }
  },

  // ─── Download and trigger browser save dialog ────────────────────────────
  async downloadAndSaveDeckFile(filePath, fileName = null) {
    try {
      const blob = await this.downloadDeckFile(filePath);

      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href  = url;

      if (!fileName) {
        const parts = filePath.split('/');
        fileName = parts[parts.length - 1];
      }

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('❌ Error downloading and saving deck file:', error);
      throw error;
    }
  },

  // ─── Delete file ─────────────────────────────────────────────────────────
  async deleteDeckFile(filePath) {
    try {
      if (!filePath) throw new Error('File path is required');

      console.log('🗑️ Deleting deck file:', filePath);

      const { error } = await supabase
        .storage
        .from('deck-files')
        .remove([filePath]);

      if (error) throw error;

      console.log('✅ Deck file deleted successfully');

      return true;
    } catch (error) {
      console.error('❌ Error deleting deck file:', error);
      throw error;
    }
  },

  // ─── List all files for a user ────────────────────────────────────────────
  async listUserFiles(userId) {
    try {
      if (!userId) throw new Error('User ID is required');

      console.log('📋 Listing files for user:', userId);

      const { data: years, error: yearsError } = await supabase
        .storage
        .from('deck-files')
        .list('', { limit: 100 });

      if (yearsError) throw yearsError;

      const allFiles    = [];
      const yearFolders = years?.filter(item => item.id === null) || [];

      for (const year of yearFolders) {
        const { data: months, error: monthsError } = await supabase
          .storage
          .from('deck-files')
          .list(year.name, { limit: 100 });

        if (monthsError) continue;

        const monthFolders = months?.filter(item => item.id === null) || [];

        for (const month of monthFolders) {
          const folderPath = `${year.name}/${month.name}/${userId}`;

          const { data: files, error: filesError } = await supabase
            .storage
            .from('deck-files')
            .list(folderPath, {
              limit: 100,
              sortBy: { column: 'created_at', order: 'desc' },
            });

          if (filesError) continue;

          (files || []).forEach(file => {
            allFiles.push({
              ...file,
              fullPath: `${folderPath}/${file.name}`,
              year:      year.name,
              month:     month.name,
            });
          });
        }
      }

      // Also check legacy format (userId directly in root)
      const { data: oldFiles, error: oldError } = await supabase
        .storage
        .from('deck-files')
        .list(userId, { limit: 100 });

      if (!oldError && oldFiles) {
        oldFiles.forEach(file => {
          allFiles.push({
            ...file,
            fullPath: `${userId}/${file.name}`,
            isLegacy: true,
          });
        });
      }

      return allFiles;
    } catch (error) {
      console.error('❌ Error listing user files:', error);
      throw error;
    }
  },

  // ─── Get file metadata ────────────────────────────────────────────────────
  async getFileMetadata(filePath) {
    try {
      if (!filePath) throw new Error('File path is required');

      const { data, error } = await supabase
        .storage
        .from('deck-files')
        .info(filePath);

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('❌ Error getting file metadata:', error);
      throw error;
    }
  },

  // ─── Copy file ────────────────────────────────────────────────────────────
  async copyDeckFile(sourcePath, destinationPath) {
    try {
      if (!sourcePath || !destinationPath)
        throw new Error('Source and destination paths are required');

      console.log('📋 Copying file from', sourcePath, 'to', destinationPath);

      const { data, error } = await supabase
        .storage
        .from('deck-files')
        .copy(sourcePath, destinationPath);

      if (error) throw error;

      console.log('✅ File copied successfully');

      return data;
    } catch (error) {
      console.error('❌ Error copying file:', error);
      throw error;
    }
  },

  // ─── Update / replace file ────────────────────────────────────────────────
  async updateDeckFile(filePath, file) {
    try {
      if (!filePath || !file)
        throw new Error('File path and file are required');

      console.log('🔄 Updating file:', filePath);

      const { data, error } = await supabase
        .storage
        .from('deck-files')
        .update(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      console.log('✅ File updated successfully');

      return data;
    } catch (error) {
      console.error('❌ Error updating file:', error);
      throw error;
    }
  },

  // ─── Migrate legacy file to new structure ────────────────────────────────
  async migrateFileToNewStructure(oldPath, userId) {
    try {
      console.log('🔄 Migrating file:', oldPath);

      const blob = await this.downloadDeckFile(oldPath);
      const file = new File([blob], oldPath.split('/').pop(), { type: blob.type });

      const result = await this.uploadDeckFile(file, userId);
      await this.deleteDeckFile(oldPath);

      console.log('✅ File migrated successfully:', result.path);

      return result;
    } catch (error) {
      console.error('❌ Error migrating file:', error);
      throw error;
    }
  },
};

export default storageService;